"use client";

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { AnimatePresence, type MotionStyle, motion } from "framer-motion";
import Link from "next/link";
import type * as React from "react";
import type { CSSProperties } from "react";
import { useId, useMemo, useState } from "react";
import { type AllIconKey, Icon } from "@/components/assets/icon/icons";
import ExpandableModal from "@/components/layout/modal/expandable-modal";
import { cn } from "@/lib/_utils";

// Helper to get RGB from CSS variable at runtime
// This converts CSS variables to RGB strings for use in MeshGradient
const getComputedColor = (varName: string): string => {
  if (typeof window === "undefined") {
    // SSR fallback - return placeholder
    return "rgb(59, 130, 246)";
  }
  const computed = getComputedStyle(document.documentElement)
    .getPropertyValue(`--color-${varName}`)
    .trim();

  // If it's already RGB, return it
  if (computed.startsWith("rgb")) {
    return computed;
  }

  // For oklch, we need to convert - for now return a temp element to get computed RGB
  const temp = document.createElement("div");
  temp.style.color = computed;
  temp.style.position = "absolute";
  temp.style.visibility = "hidden";
  document.body.appendChild(temp);
  const rgb = getComputedStyle(temp).color;
  document.body.removeChild(temp);
  return rgb || "rgb(59, 130, 246)"; // fallback
};

const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 active:translate-y-px active:scale-[0.98] active:bg-primary/95",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 active:translate-y-px active:scale-[0.98] active:bg-destructive/95 dark:bg-destructive/60 dark:active:bg-destructive/70 dark:focus-visible:ring-destructive/40",
        outline:
          "border-2 border-gray-300 bg-transparent text-gray-700 shadow-sm hover:border-gray-400 hover:bg-gray-50 hover:text-gray-900 active:translate-y-px active:scale-[0.98] active:border-gray-500 active:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:active:border-gray-400 dark:active:bg-gray-900 dark:hover:border-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-100",
        secondary:
          "border border-gray-400/30 bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 active:translate-y-px active:scale-[0.98] active:bg-secondary/90",
        ghost:
          "hover:bg-accent/40 hover:text-accent-foreground active:translate-y-px active:scale-[0.98] active:bg-accent/50 dark:active:bg-accent/40 dark:hover:bg-accent/30",
        link: "text-primary underline-offset-4 hover:underline active:scale-[0.98] active:opacity-80",
      },
      size: {
        xs: "h-6 rounded-md px-3 font-medium text-xs",
        sm: "h-8 rounded-2xl px-4 font-medium text-sm has-[>svg]:px-8",
        md: "h-10 rounded-full px-6 font-medium text-sm has-[>svg]:px-4",
        lg: "h-12 rounded-full px-6 font-medium text-sm has-[>svg]:px-4",
        xl: "h-14 rounded-full px-8 font-medium text-lg has-[>svg]:px-6",
        "2xl": "h-16 rounded-full px-10 font-medium text-xl has-[>svg]:px-8",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

// Color presets for shimmer and gradient effects
export type ButtonColor = "primary" | "secondary" | "tertiary";

// Helper to get CSS variable string
const getColorVar = (varName: string) => `var(--color-${varName})`;

// Shimmer presets using your custom colors
// Uses your custom-blue and custom-yellow colors - no need to type full names!
// Just use: "custom-blue", "custom-yellow", "custom-blue-dark", etc.
const createShimmerPresets = (): Record<
  ButtonColor,
  { colors: string[]; background: string; shimmerColor: string }
> => {
  // Compute colors at runtime from CSS variables
  return {
    primary: {
      colors: [
        getComputedColor("custom-blue"),
        getComputedColor("custom-blue-dark"),
        getComputedColor("custom-blue"),
        getComputedColor("custom-blue-light"),
      ],
      background: `linear-gradient(45deg, ${getColorVar("custom-blue")}, ${getColorVar("custom-blue-dark")})`,
      shimmerColor: "rgb(255, 255, 255)", // white
    },
    secondary: {
      colors: [
        getComputedColor("custom-yellow"),
        getComputedColor("custom-yellow-dark"),
        getComputedColor("custom-yellow"),
        getComputedColor("custom-yellow"),
      ],
      background: `linear-gradient(45deg, ${getColorVar("custom-yellow")}, ${getColorVar("custom-yellow-dark")})`,
      shimmerColor: "rgb(255, 255, 255)", // white
    },
    tertiary: {
      colors: [
        getComputedColor("custom-blue-light"),
        getComputedColor("custom-blue"),
        getComputedColor("custom-blue-light"),
        getComputedColor("custom-blue"),
      ],
      background: `linear-gradient(45deg, ${getColorVar("custom-blue-light")}, ${getColorVar("custom-blue")})`,
      shimmerColor: "rgb(255, 255, 255)", // white
    },
  };
};

// Gradient presets using your custom colors
// Uses Tailwind classes - just reference custom-blue, custom-yellow, etc.
const GRADIENT_PRESETS: Record<ButtonColor, string> = {
  primary: "bg-linear-to-r from-custom-blue to-custom-blue-dark",
  secondary: "bg-linear-to-r from-custom-yellow to-custom-yellow-dark",
  tertiary: "bg-linear-to-r from-custom-blue-light to-custom-blue",
};

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    href?: string;
    iconBefore?: React.ReactNode;
    iconAfter?: React.ReactNode;
    /**
     * Icon name from the icons library or lucide-react (e.g., "user", "search", "DrillIcon", "Home")
     * This will render before the button text. Takes precedence over iconBefore if both are provided.
     * Supports both custom icons and lucide-react icons without manual imports.
     */
    iconName?: AllIconKey;
    /**
     * Icon name from the icons library or lucide-react to render after the button text.
     * Takes precedence over iconAfter if both are provided.
     * Supports both custom icons and lucide-react icons without manual imports.
     */
    iconAfterName?: AllIconKey;
    /**
     * Size for icons when using iconName or iconAfterName
     */
    iconSize?: "sm" | "md" | "lg";
    textColor?: string;
    /**
     * Color preset for shimmer and gradient effects
     */
    color?: ButtonColor;
    /**
     * Enable gradient background (uses color preset or custom gradient)
     */
    gradient?: boolean | string | string[];
    /**
     * Enable shimmer effect (uses color preset for colors)
     */
    shimmer?: boolean;
    shimmerColor?: string;
    shimmerSize?: string;
    shimmerDuration?: string;
    borderRadius?: string;
    background?: string;
    expandable?: boolean;
    expandableContent?: React.ReactNode;
    expandableProps?: {
      backgroundType?: "solid" | "mesh";
      backgroundColor?: string;
      meshColor?:
        | "primary"
        | "secondary"
        | "tertiary"
        | "quaternary"
        | "blue"
        | "blueDark"
        | "yellow"
        | "yellowDark"
        | "cloud"
        | "cloudDark";
      showCloseButton?: boolean;
      fullWidth?: boolean;
      onOpen?: () => void;
      onClose?: () => void;
    };
  };

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  href,
  iconBefore,
  iconAfter,
  iconName,
  iconAfterName,
  iconSize = "md",
  textColor,
  color = "primary",
  gradient,
  shimmer,
  shimmerColor,
  shimmerSize = "0.05em",
  shimmerDuration = "3s",
  borderRadius = "100px",
  background,
  expandable = false,
  expandableContent,
  expandableProps,
  children,
  onClick,
  ref,
  ...props
}: ButtonProps & { ref?: React.RefObject<HTMLButtonElement | null> }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const layoutId = useId();

  // Handle expandable modal
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (expandable) {
      setIsModalOpen(true);
    }
    onClick?.(e);
  };

  // Get background style for expandable buttons
  const getExpandableBackgroundStyle = (): React.CSSProperties => {
    if (!(expandable && expandableProps)) {
      return {};
    }
    const { backgroundType, backgroundColor } = expandableProps;
    if (backgroundType === "solid" && backgroundColor) {
      return { backgroundColor } as React.CSSProperties;
    }
    return {} as React.CSSProperties;
  };

  // Render icon before text (iconName takes precedence over iconBefore)
  const renderIconBefore = () => {
    if (iconName) {
      return <Icon name={iconName} size={iconSize} />;
    }
    return iconBefore;
  };

  // Render icon after text (iconAfterName takes precedence over iconAfter)
  const renderIconAfter = () => {
    if (iconAfterName) {
      return <Icon name={iconAfterName} size={iconSize} />;
    }
    return iconAfter;
  };

  // Get shimmer preset based on color (computed at runtime to get RGB from CSS variables)
  const shimmerPreset = useMemo(() => createShimmerPresets()[color], [color]);
  const finalShimmerColor = shimmerColor || shimmerPreset.shimmerColor;
  const finalBackground = background || shimmerPreset.background;

  // Build gradient class
  let gradientClass = "";
  if (gradient) {
    if (typeof gradient === "boolean" && gradient) {
      // Use color preset gradient
      gradientClass = GRADIENT_PRESETS[color];
    } else if (typeof gradient === "string") {
      // Custom gradient class
      gradientClass = gradient;
    } else if (Array.isArray(gradient)) {
      // Custom gradient colors
      gradientClass = `bg-linear-to-r from-${gradient[0]} to-${gradient[1]}`;
    }
  }

  // Build text color class
  const textColorClass = textColor ? `text-${textColor}` : "";

  // Shimmer effect rendering (works with any variant)
  if (shimmer) {
    const shimmerStyles = {
      "--spread": "90deg",
      "--shimmer-color": finalShimmerColor,
      "--radius": borderRadius,
      "--speed": shimmerDuration,
      "--cut": shimmerSize,
      "--bg": finalBackground,
    } as CSSProperties;

    const shimmerButton = (
      <button
        className={cn(
          "group relative z-0 flex cursor-pointer items-center justify-center overflow-hidden whitespace-nowrap border border-white/10 px-6 py-3 text-white [background:var(--bg)] [border-radius:var(--radius)] dark:text-black",
          "transform-gpu transition-all duration-300 ease-in-out",
          "hover:scale-105 hover:border-white/20 hover:brightness-110",
          "active:translate-y-px active:scale-100",
          size === "xs" && "h-6 px-3 text-xs",
          size === "sm" && "h-8 px-4 text-sm",
          size === "md" && "h-10 px-6 text-base",
          size === "lg" && "h-12 px-8 text-lg",
          size === "xl" && "h-14 px-10 text-xl",
          size === "2xl" && "h-16 px-12 text-2xl",
          textColorClass,
          className
        )}
        onClick={handleClick}
        ref={ref}
        style={shimmerStyles}
        {...props}
      >
        {/* spark container */}
        <div
          className={cn(
            "-z-30 blur-[2px]",
            "@container-[size] absolute inset-0 overflow-visible",
            "transition-opacity duration-300",
            "group-hover:opacity-100 group-hover:blur-[3px]"
          )}
        >
          {/* spark */}
          <div className="group-hover:animation-duration-[2s] absolute inset-0 aspect-[1] h-[100cqh] animate-shimmer-slide rounded-none [mask:none]">
            {/* spark before */}
            <div className="-inset-full absolute w-auto rotate-0 animate-spin-around [background:conic-gradient(from_calc(270deg-(var(--spread)*0.5)),transparent_0,var(--shimmer-color)_var(--spread),transparent_var(--spread))] [translate:0_0] group-hover:opacity-100" />
          </div>
        </div>

        <span className="relative z-10 flex items-center gap-2 transition-transform duration-300 [text-shadow:0_1px_3px_rgba(0,0,0,0.4),0_1px_1px_rgba(0,0,0,0.3)] group-hover:scale-105">
          {renderIconBefore()}
          {children}
          {renderIconAfter()}
        </span>

        {/* Highlight */}
        <div
          className={cn(
            "insert-0 absolute size-full",
            "rounded-2xl px-4 py-1.5 font-medium text-sm shadow-[inset_0_-8px_10px_#ffffff1f]",
            "transform-gpu transition-all duration-300 ease-in-out",
            "group-hover:shadow-[inset_0_-6px_10px_#ffffff4f] group-hover:brightness-110",
            "group-active:shadow-[inset_0_-10px_10px_#ffffff3f]"
          )}
        />

        {/* Hover glow effect */}
        <div
          className={cn(
            "absolute inset-0 rounded-(--radius) opacity-0 transition-opacity duration-300",
            "group-hover:opacity-100 group-hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]",
            "pointer-events-none"
          )}
        />

        {/* backdrop */}
        <div
          className={cn(
            "-z-20 absolute inset-(--cut) [background:var(--bg)] [border-radius:var(--radius)]"
          )}
        />
      </button>
    );

    if (expandable) {
      const backgroundStyle = getExpandableBackgroundStyle();
      const {
        backgroundType,
        meshColor,
        onClose: userOnClose,
        ...restExpandableProps
      } = expandableProps || {};

      return (
        <>
          <AnimatePresence initial={false}>
            {!isModalOpen && (
              <motion.div className="relative inline-block">
                {/* Morphing background */}
                <motion.div
                  className="absolute inset-0 transform-gpu items-center justify-center overflow-hidden will-change-transform"
                  layout
                  layoutId={layoutId}
                  style={
                    {
                      borderRadius: "100px",
                      ...backgroundStyle,
                    } as MotionStyle
                  }
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  {/* Mesh gradient background if needed */}
                  {backgroundType === "mesh" && meshColor && (
                    <div className="absolute inset-0">
                      <div
                        className="absolute inset-0"
                        style={{ borderRadius: "100px" }}
                      />
                    </div>
                  )}
                </motion.div>
                {/* Shimmer button content */}
                <motion.div
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative"
                  exit={{ opacity: 0, scale: 0.8 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  layout={false}
                  transition={{ delay: 0.2, duration: 0.3 }}
                >
                  {shimmerButton}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          <ExpandableModal
            hideButton={true}
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              userOnClose?.();
            }}
            {...restExpandableProps}
          >
            {expandableContent}
          </ExpandableModal>
        </>
      );
    }

    return shimmerButton;
  }

  // Regular button rendering
  const baseClasses = cn(
    buttonVariants({ variant, size }),
    gradientClass, // Apply gradient if enabled
    textColorClass,
    // Add text shadow for gradient buttons with white text to improve readability
    gradient &&
      !textColor &&
      "[text-shadow:0_1px_3px_rgba(0,0,0,0.4),0_1px_1px_rgba(0,0,0,0.3)]",
    className
  );

  // If href is provided, render as Link
  if (href) {
    const linkContent = (
      <>
        {renderIconBefore()}
        {children}
        {renderIconAfter()}
      </>
    );

    // Filter out button-specific props for Link - only pass common HTML attributes
    const linkProps: React.ComponentProps<typeof Link> = {
      href,
      className: baseClasses,
      ...(expandable && {
        onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          handleClick(e as unknown as React.MouseEvent<HTMLButtonElement>);
        },
      }),
      ...(!expandable &&
        onClick && {
          onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
            onClick(e as unknown as React.MouseEvent<HTMLButtonElement>);
          },
        }),
      ...(props.disabled && { "aria-disabled": true }),
    };

    if (expandable) {
      const backgroundStyle = getExpandableBackgroundStyle();
      const {
        backgroundType,
        meshColor,
        onClose: userOnClose,
        ...restExpandableProps
      } = expandableProps || {};

      return (
        <>
          <AnimatePresence initial={false}>
            {!isModalOpen && (
              <motion.div className="relative inline-block">
                {/* Morphing background */}
                <motion.div
                  className="absolute inset-0 transform-gpu items-center justify-center overflow-hidden will-change-transform"
                  layout
                  layoutId={layoutId}
                  style={
                    {
                      borderRadius: "100px",
                      ...backgroundStyle,
                    } as MotionStyle
                  }
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  {/* Mesh gradient background if needed */}
                  {backgroundType === "mesh" && meshColor && (
                    <div className="absolute inset-0">
                      <div
                        className="absolute inset-0"
                        style={{ borderRadius: "100px" }}
                      />
                    </div>
                  )}
                </motion.div>
                {/* Link content */}
                <motion.div
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative"
                  exit={{ opacity: 0, scale: 0.8 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  layout={false}
                  transition={{ delay: 0.2, duration: 0.3 }}
                >
                  <Link {...linkProps}>{linkContent}</Link>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          <ExpandableModal
            hideButton={true}
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              userOnClose?.();
            }}
            {...restExpandableProps}
          >
            {expandableContent}
          </ExpandableModal>
        </>
      );
    }

    return <Link {...linkProps}>{linkContent}</Link>;
  }

  // Regular button
  const Comp = asChild ? Slot : "button";

  const buttonContent = (
    <Comp className={baseClasses} onClick={handleClick} ref={ref} {...props}>
      {renderIconBefore()}
      {children}
      {renderIconAfter()}
    </Comp>
  );

  if (expandable) {
    const backgroundStyle = getExpandableBackgroundStyle();
    const {
      backgroundType,
      meshColor,
      onClose: userOnClose,
      ...restExpandableProps
    } = expandableProps || {};

    return (
      <>
        <AnimatePresence initial={false}>
          {!isModalOpen && (
            <motion.div className="relative inline-block">
              {/* Morphing background */}
              <motion.div
                className="absolute inset-0 transform-gpu items-center justify-center overflow-hidden will-change-transform"
                layout
                layoutId={layoutId}
                style={
                  {
                    borderRadius: "100px",
                    ...backgroundStyle,
                  } as MotionStyle
                }
                transition={{ duration: 0.6, ease: "easeInOut" }}
              >
                {/* Mesh gradient background if needed */}
                {backgroundType === "mesh" && meshColor && (
                  <div className="absolute inset-0">
                    {/* We'll use the MeshGradientBackground from the modal */}
                    <div
                      className="absolute inset-0"
                      style={{ borderRadius: "100px" }}
                    />
                  </div>
                )}
              </motion.div>
              {/* Button content */}
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                className="relative"
                exit={{ opacity: 0, scale: 0.8 }}
                initial={{ opacity: 0, scale: 0.8 }}
                layout={false}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                {buttonContent}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <ExpandableModal
          hideButton={true}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            userOnClose?.();
          }}
          {...restExpandableProps}
        >
          {expandableContent}
        </ExpandableModal>
      </>
    );
  }

  return buttonContent;
};

Button.displayName = "Button";

export { Button, buttonVariants };
